import React, {useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";

// ThatOpen / IFC.js
import * as OBF from "@thatopen/components-front";
import * as THREE from "three";

import { DrawVectorTool, PlanOrigin } from "@treegram/tools-draw-vector";
import { ImgLoader } from "@treegram/loaders-img";
import { PlanDataDetailsRef } from "./plan-data.tsx";

import useProjectFilePlanContext from "./context/use-project-file-plan-context.ts";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import ViewerCanvas from "@/services/context/viewer/instance/viewer-canvas.tsx";

import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import PlanFormat from "@/pages/takeoff/context/project-organisation/types/plan-format.ts";
import PlanScale from "@/pages/takeoff/context/project-organisation/types/plan-scale.ts";
import PlanOrientation from "@/pages/takeoff/context/project-organisation/types/plan-orientation.ts";

interface OrganisationPlanViewerContainerProps {
    planDataDetailsRef: React.RefObject<PlanDataDetailsRef>;
}

export interface OrganisationPlanViewerContainerRef {
    updateLoaderOptions: (mode: "KnownScaleContent" | "UserDefinedContent", knownFormat?: PlanFormat, knownScale?: PlanScale, knownOrientation?: PlanOrientation, definedscale?: number) => void;
    clearDrawingTool: () => Promise<void>;
    applyNewScale: (newScale: number) => Promise<void>;    
}

const OrganisationPlanViewerContainer = React.forwardRef<OrganisationPlanViewerContainerRef, OrganisationPlanViewerContainerProps>(({ planDataDetailsRef }, ref) => {
    const { dataContent, setDefinedScale } = useProjectFilePlanContext();
    const { isInDrawingMode, setIsInDrawingMode } = useModelingToolsContext();
    const { isLoading, getTool, getLoader } = useViewerContext();

    const segmentInitializedRef = useRef(false);
    const [showLoading, setShowLoading] = useState(true);

    useEffect(() => {
        // Ignore idle states before the plan has actually loaded.
        if (!isLoading && getLoader(ImgLoader)?.getDimensionsCanvas()) {
            setShowLoading(false);
        }
    }, [isLoading, getLoader]);

    // Gestion lorsque le viewer a fini de dessiner un trait
    const vectorDrawnEndedCallback = useCallback(async (planOrigin: PlanOrigin) => {
        const drawVectorTool = getTool(DrawVectorTool);
        if (drawVectorTool == null) return;

        setIsInDrawingMode(false);
        drawVectorTool.onEndedCreation.remove(vectorDrawnEndedCallback);
        await drawVectorTool.deactivate();

        planDataDetailsRef.current?.setLine(planOrigin.line);
    }, [getTool, setIsInDrawingMode, planDataDetailsRef]);

    useImperativeHandle(ref, () => ({
        updateLoaderOptions: (_mode: "KnownScaleContent" | "UserDefinedContent", knownFormat?: PlanFormat, knownScale?: PlanScale, knownOrientation?: PlanOrientation, definedScale?: number) => {
            const loaderImg = getLoader(ImgLoader);
            if (!loaderImg) return;

            const options: {
                format?: PlanFormat;
                scale?: PlanScale;
                orientation?: PlanOrientation;
                definedScale?: number;
            } = {};

            if (knownFormat !== undefined)
                options.format = knownFormat;
            if (knownScale !== undefined)
                options.scale = knownScale;
            if (knownOrientation !== undefined)
                options.orientation = knownOrientation;
            if (definedScale !== undefined)
                options.definedScale = definedScale;

            loaderImg.updateOptions(options);

            const dimensionsCanvas = loaderImg.getDimensionsCanvas();
            const dimensionsPrint = loaderImg.getDimensionsPrint();

            if (dimensionsCanvas && dimensionsPrint) {
                planDataDetailsRef.current?.setIsScaleInvalid(
                    Math.abs(
                        dimensionsCanvas.width / dimensionsCanvas.height -
                        dimensionsPrint.width / dimensionsPrint.height
                    ) > 0.001
                );
                if (_mode === "UserDefinedContent") {
                    setDefinedScale(
                        Math.round(
                            dimensionsCanvas.width / dimensionsPrint.width * 1000
                        ) / 1000
                    );
                }
            }
        },
        clearDrawingTool: async () => {
            const drawingTool = getTool(DrawVectorTool);
            if (drawingTool) {
                drawingTool.onEndedCreation.remove(vectorDrawnEndedCallback);
                await drawingTool.deactivate();
                drawingTool.clear();
            }
            setIsInDrawingMode(false);
        },
        applyNewScale: async (newScale: number) => {
            const drawingTool = getTool(DrawVectorTool);
            if (drawingTool) {
                drawingTool.onEndedCreation.remove(vectorDrawnEndedCallback);

                const measures = drawingTool.getMeasures();
                const line = [...measures.values()][measures.size - 1];
                drawingTool.clear();

                const newStart = new THREE.Vector3(line.start.x * newScale, 0, line.start.z * newScale);
                const newEnd = new THREE.Vector3(line.end.x * newScale, 0, line.end.z * newScale);
                drawingTool.addMeasure(new OBF.Line(newStart, newEnd));

                await drawingTool.deactivate();
            }
            setIsInDrawingMode(false);
        }
    }), [getTool, getLoader, vectorDrawnEndedCallback, setIsInDrawingMode, setDefinedScale, planDataDetailsRef]);

    // Affichage du trait sur le viewer
    useEffect(() => {
        const drawVectorTool = getTool(DrawVectorTool);
        if (isLoading || !drawVectorTool) return;
        if (segmentInitializedRef.current) return;
        if (!(dataContent instanceof UserDefinedContent)) return;

        drawVectorTool.clear();

        if (dataContent.segment) {
            drawVectorTool.addMeasure(new OBF.Line(
                new THREE.Vector3(dataContent.segment.start.x, 0, -dataContent.segment.start.y),
                new THREE.Vector3(dataContent.segment.end.x, 0, -dataContent.segment.end.y)
            ));
        }

        segmentInitializedRef.current = true;
    }, [isLoading, dataContent, getTool]);

    useEffect(() => {
        const drawVectorTool = getTool(DrawVectorTool);
        let isMounted = true;

        const run = async () => {
            if (!drawVectorTool) return;
            if (isInDrawingMode) {
                if (!isMounted) return;
                await drawVectorTool.activate();
                drawVectorTool.clear();
                drawVectorTool.draw();
                drawVectorTool.onEndedCreation.add(vectorDrawnEndedCallback);
            } else {
                drawVectorTool.onEndedCreation.remove(vectorDrawnEndedCallback);
                await drawVectorTool.deactivate();
            }
        };

        run();

        return () => {
            isMounted = false;
            drawVectorTool?.onEndedCreation.remove(vectorDrawnEndedCallback);
        };
    }, [isInDrawingMode, vectorDrawnEndedCallback, getTool]);

    return (
        <div className="absolute inset-0 overflow-hidden">
            <ViewerCanvas loadingText="Chargement du plan" showLoading={showLoading} />
        </div>
    );
});

export default OrganisationPlanViewerContainer;
